import {
  buildCollection,
  buildProperty,
  type CollectionActionsProps,
  type EntityCollection,
} from "@firecms/core";
import { getAccountPath, type AccountCollectionId } from "../utils";
import {
  type LocalizedString,
  buildLocalizedStringProperty,
} from "../localization";
import { buildContentEditorEntityAction } from "../actions/buildContentEditorEntityAction";
import { buildCloneSpaceEntityAction } from "../actions/buildCloneSpaceEntityAction";
import { buildImportLearningPathEntityAction } from "../actions/buildImportLearningPathEntityAction";
import { ImportLearningPathJsonButton } from "../actions/ImportLearningPathJsonButton";
import { NAV_GROUP_CONTENT_SESSIONS } from "./navigationGroups";

export type Space = {
  name: string;
  title: LocalizedString;
  body: LocalizedString;
  banner?: string;
};

export const buildSpacesPath = (id: AccountCollectionId) =>
  `${getAccountPath(id)}/spaces`;

const buildPath = buildSpacesPath;

const collection: EntityCollection<Space> = {
  id: "spaces",
  name: "Learning Paths",
  singularName: "Learning Path",
  description:
    "The content you prepare before anyone joins: what they will read and practice in Learn, and the missions they will play for points. You can reuse the same learning path for different live sessions.",
  path: "spaces",
  titleProperty: "name",
  previewProperties: ["name"],
  icon: "CalendarMonth",
  group: NAV_GROUP_CONTENT_SESSIONS,
  permissions: () => ({
    read: true,
    edit: true,
    create: true,
    delete: true,
  }),
  properties: {
    name: buildProperty({
      name: "Name",
      validation: { required: true },
      dataType: "string",
    }),
    title: buildLocalizedStringProperty({
      name: "Title",
      dataType: "string",
      validation: { required: true },
    }),
    body: buildLocalizedStringProperty({
      name: "Body",
      dataType: "string",
      markdown: true,
      description:
        "Introductory text about this learning path. Participants may see it in the app depending on your setup.",
      validation: { required: true },
    }),
    banner: buildProperty({
      name: "Banner",
      description:
        "3:1 image copied onto new sessions. Uploads are center-cropped, resized, and saved as JPEG.",
      dataType: "string",
      validation: { required: false },
      storage: {
        storagePath: "images",
        acceptedFiles: ["image/*"],
        maxSize: 1024 * 1024,
        metadata: {
          cacheControl: "max-age=1000000",
        },
        imageResize: {
          maxWidth: 1200,
          maxHeight: 400,
          mode: "cover",
          format: "jpeg",
          quality: 75,
        },
        fileName: (context) => {
          const base =
            context.file.name.replace(/\.[^.]+$/, "") || "banner";
          return `${base}.jpg`;
        },
      },
    }),
  },
};

export const spacesCollection = buildCollection<Space>(collection);

export const buildSpacesCollection = (id: AccountCollectionId) => {
  return buildCollection<Space>({
    ...collection,
    defaultSize: "xs",
    path: buildPath(id),
    Actions: ({ collection: actionCollection }: CollectionActionsProps<Space>) => [
      <ImportLearningPathJsonButton
        key="import-learning-path"
        accountId={id.accountId}
        collection={actionCollection as EntityCollection<Space>}
      />,
    ],
    entityActions: [
      buildContentEditorEntityAction(id.accountId),
      buildCloneSpaceEntityAction(id.accountId),
      buildImportLearningPathEntityAction(id.accountId),
    ],
  });
};
